import { Collapse, useMediaQuery } from '@mui/material';
import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Avatar, Box, Icon, Paper, Progress, Typography } from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { usePostAuditViewProfile } from '../../../api-refactored/user/usePostAuditViewProfile';
import { usePostLogout } from '../../../api-refactored/user/usePostLogout';
import { useBaseRole } from '../../../components/DefaultHeader';
import { useHover } from '../../../components/menu/DesktopMenu';
import { useUserDetails } from '../../../hooks/userDetails';
import { Role } from '../../../models';
import RouteKey from '../../../routes/key';
import { selectIsUserLoggedIn } from '../../../store/appSlice';
import { UpdateFormEdit } from '../../../store/formSlice';
import { useAuth } from '../logic/auth';
import {typeTheme} from "web-library/theme/helper/tssHelper"

const useStyles = makeStyles()((theme) =>
({
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    marginRight: theme.spacing(2),
  },
  profileContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  profileButtonContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  edgeAnchor: {
    position: 'relative',
  },
  menuPaper: {
    zIndex: 4,
    position: 'absolute',
    width: 250,
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: theme.color.background.bgAlt,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    boxShadow: theme.shadow.shadow3,
    [theme.breakpoints.down('md')]: {
      width: 200,
      left: '100%',
      transform: 'translateX(-100%)',
    },
  },
  innerMenuPaper: {
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  subMenuItem: {
    cursor: 'pointer',
    height: theme.spacing(7.5),
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    color: theme.color.text.textHeader,
    borderRadius: theme.spacing(2),
    '&:hover': {
      color: theme.color.text.textHeader,
      backgroundColor: theme.color.background.bgMain,
    },
    '&:active': {
      color: theme.color.text.textActive,
      backgroundColor: theme.color.background.bgHover01,
    },
     ...typeTheme(theme, 'body1bold'),
    [theme.breakpoints.down('md')]: {
      height: theme.spacing(7),
    },
    [theme.breakpoints.down('sm')]: {
      height: theme.spacing(6),
    },
  },
  subMenuLabel: {
    fontWeight: 700,
    userSelect: 'none',
    msUserSelect: 'none',
    MozUserSelect: 'none',
    WebkitUserSelect: 'none',
  },
  menuItemIcon: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  profileChip: {
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderRadius: theme.spacing(2),
    '&:hover': {
      backgroundColor: theme.color.border.borderFocused,
    },
    '&:active': {
      backgroundColor: theme.color.background.bgDisabled,
    },

    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(0.5),
    },
  },
  avatar: {
    marginRight: theme.spacing(1),

    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
    },
  },
  profileButton: {
    display: 'block',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    color: theme.color.text.textHeader,
    maxWidth: 150,
    userSelect: 'none',
    msUserSelect: 'none',
    MozUserSelect: 'none',
    WebkitUserSelect: 'none',
     ...typeTheme(theme, 'body1bold'),
  },
  loadingContainer: {
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  loading: {
    color: theme.color.icon.iconDefault,
  },
}));

const { auth: authRoute, profile } = RouteKey.child;
const { login } = RouteKey.auth.child;
const {
  candidateDetail,
  adminDetail,
  coordinatorDetail,
  detail,
  settings,
} = RouteKey.profile.child;

const HeaderUserDetails = () => {
  const { classes } = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const notDesktop = useMediaQuery(theme.breakpoints.down('md'));

  const auth = useAuth();
  const { data, isSuccess, isFetching } = useUserDetails();
  const loggedIn = useSelector(selectIsUserLoggedIn);
  const { mutate: logout } = usePostLogout({
    refreshPage: false,
    redirectToHome: true,
  });

  const baseRole = useBaseRole(loggedIn, isSuccess, data?.data.roles);
  const isCpAccount = data?.data.credentialType === 'USERNAME';

  const history = useHistory();

  const detailMenu = useHover();

  const viewProfile = usePostAuditViewProfile();

  const menuOnLoginCheck = (userLoggedIn: boolean) => {
    if (userLoggedIn)
      return (
        <>
          {baseRole && baseRole !== 'ADMIN' && (
            <Box
              id="menu-user-submenu-myProfile"
              className={classes.subMenuItem}
              onClick={() => {
                detailMenu.handleClose();
                if (data?.data.roles?.includes(Role.candidate)) {
                  dispatch(UpdateFormEdit(false));
                  history.push(`/${profile}/${candidateDetail.route}`);
                } else if (data?.data.roles?.includes(Role.coordinator)) {
                  dispatch(UpdateFormEdit(false));
                  history.push(`/${profile}/${coordinatorDetail.route}`);
                  viewProfile.mutate();
                } else {
                  history.push(`/${profile}/${detail.route}`);
                }
              }}
            >
              <Typography variant="caption" className={classes.subMenuLabel}>
                My Profile
              </Typography>
            </Box>
          )}
          {baseRole && baseRole === 'ADMIN' && (
            <Box
              id="menu-user-submenu-myProfile"
              className={classes.subMenuItem}
              onClick={() => {
                detailMenu.handleClose();
                if (data?.data.roles?.includes(Role.opsAdmin)) {
                  dispatch(UpdateFormEdit(false));
                  history.push(`/${profile}/${adminDetail.route}`);
                } else {
                  history.push(`/${profile}/${detail.route}`);
                }
              }}
            >
              <Typography variant="caption" className={classes.subMenuLabel}>
                My Profile
              </Typography>
            </Box>
          )}
          {isCpAccount && (
            <Box
              id="menu-user-submenu-settings"
              className={classes.subMenuItem}
              onClick={() => {
                detailMenu.handleClose();
                history.push(`/${profile}/${settings.route}`);
              }}
            >
              <Typography variant="caption" className={classes.subMenuLabel}>
                Settings
              </Typography>
            </Box>
          )}

          <Box
            id="menu-user-submenu-logout"
            className={classes.subMenuItem}
            onClick={() => {
              detailMenu.handleClose();
              logout();
            }}
          >
            <Typography variant="caption" className={classes.subMenuLabel}>
              Log Out
            </Typography>
          </Box>
        </>
      );
    return (
      <>
        <Box
          id="menu-user-submenu-login"
          className={classes.subMenuItem}
          onClick={() => {
            detailMenu.handleClose();
            history.push(`/${authRoute}/${login}`);
          }}
        >
          <Typography variant="caption" className={classes.subMenuLabel}>
            Log In
          </Typography>
        </Box>
      </>
    );
  };

  return (
    <Box className={classes.container}>
      {isFetching ? (
        <Box className={classes.loadingContainer}>
          <Progress color="secondary" />
        </Box>
      ) : (
        <>
          <Box className={classes.profileContainer}>
            <Box className={classes.profileButtonContainer}>
              <Box
                className={classes.profileChip}
                onMouseOver={(evt) => {
                  detailMenu.handleHover(evt);
                }}
                onMouseLeave={detailMenu.handleEndHoverClose}
                id="menu-user"
              >
                <Avatar src={data?.data?.photoURL} className={classes.avatar} type="icon" />

                {!notDesktop && (
                  <>
                    <Typography className={classes.profileButton} noWrap>
                      {data?.data.displayName || ''}
                    </Typography>

                    <Icon
                      size="S"
                      className={classes.menuItemIcon}
                      color={theme.color.icon.iconDefault}
                      name={IconName.arrowDown}
                    />
                  </>
                )}
              </Box>
            </Box>

            <Box className={classes.edgeAnchor}>
              <Paper
                className={classes.menuPaper}
                onMouseOver={detailMenu.timerCancel}
                onMouseLeave={detailMenu.handleClose}
              >
                <Collapse in={Boolean(detailMenu.anchorEl)}>
                  <Box className={classes.innerMenuPaper}>
                    {menuOnLoginCheck(auth.isUserLoggedIn)}
                  </Box>
                </Collapse>
              </Paper>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default HeaderUserDetails;
